/**
 * React-Anbindung fuer Experimente und Rollouts auf der LP.
 *
 * Beispiel (Variante „inline" gegen Control):
 *
 *   import { useVariant } from '../experiments'
 *
 *   const HeroCta = () => {
 *     const variant = useVariant('lp_funnel_inline')
 *     // null (noch nicht geladen, unbekannt, draft) und 'control' sehen gleich aus
 *     return variant === 'inline' ? <InlineFunnel /> : <OpenFunnelButton />
 *   }
 *
 * Rollout: `const enabled = useFlag('colored_logos')` ist true bei 'on'.
 */
import { createContext, useContext, useEffect, useState } from 'react'

import { ROLLOUT_ON } from './bucketing.js'
import {
  buildContext,
  getQaOverrides,
  getState,
  IDLE_STATE,
  startExperiments,
  subscribe
} from './flags-store'
import { resolveFlag, shouldTrackExposure } from './helpers.js'
import { trackExposure } from './track-exposure'

const ExperimentsContext = createContext(IDLE_STATE)

export const ExperimentsProvider = ({ children }) => {
  // Der erste Render ist immer IDLE, genau wie das SSR-HTML. Sind die Flags
  // schon vor dem Hydrieren da, gaebe es sonst einen Hydration-Mismatch.
  const [state, setState] = useState(IDLE_STATE)

  useEffect(() => {
    startExperiments()
    setState(getState())
    return subscribe(setState)
  }, [])

  return (
    <ExperimentsContext.Provider value={state}>
      {children}
    </ExperimentsContext.Provider>
  )
}

/**
 * @returns {string|null} Variante. null, solange die Flags nicht geladen sind,
 *   bei unbekanntem Flag und bei draft. Call-Sites behandeln null wie Control.
 */
export const useVariant = (key) => {
  const { flags } = useContext(ExperimentsContext)
  const flag = flags[key]

  // Ohne Flag kein Kontext bauen, so entsteht die Besucher-ID nie im SSR.
  const ctx = flag ? buildContext() : null
  const result = flag
    ? resolveFlag(flags, key, ctx, getQaOverrides())
    : { variant: null, tracked: false }
  const track = shouldTrackExposure(flag, result)
  const visitorId = ctx?.visitorId || null

  useEffect(() => {
    if (track) trackExposure({ key, variant: result.variant, visitorId })
  }, [track, key, result.variant, visitorId])

  return result.variant
}

/** Rollout-Flag: true bei 'on'. */
export const useFlag = (key) => useVariant(key) === ROLLOUT_ON
